import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import {
  getAppOrganizerById,
  getAppOrganizers,
  topUpAppOrganizerWallet,
  updateAppOrganizerStatus,
} from '../services/api';

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const money = (value, currency = 'USD') => `${currency} ${Number(value || 0).toFixed(2)}`;

const statusBadge = status => ({
  verified: 'badge badge-green',
  pending: 'badge badge-blue',
  rejected: 'badge badge-red',
  suspended: 'badge badge-red',
})[status] || 'badge badge-gray';

const statusLabel = status => ({
  verified: 'Approved',
  pending: 'Pending',
  rejected: 'Rejected',
  suspended: 'Suspended',
})[status] || (status || 'Unknown');

const actionButtons = (organizer, onStatus, acting, size = 12) => {
  const isSuspended = organizer.verificationStatus === 'suspended' || organizer.user?.isActive === false;

  return (
    <>
      {organizer.verificationStatus !== 'verified' && (
        <button
          className="btn btn-ghost"
          disabled={acting}
          style={{ fontSize:size }}
          onClick={() => onStatus(organizer.id, { status:'verified', isActive:true, reason:'Organizer approved by admin' })}
        >
          Approve
        </button>
      )}
      {isSuspended ? (
        <button
          className="btn btn-ghost"
          disabled={acting}
          style={{ fontSize:size }}
          onClick={() => onStatus(organizer.id, { status:'pending', isActive:true, reason:'Organizer unsuspended by admin' })}
        >
          Unsuspend
        </button>
      ) : (
        <button
          className="btn btn-ghost"
          disabled={acting}
          style={{ color:'var(--danger)', fontSize:size }}
          onClick={() => onStatus(organizer.id, { status:'suspended', isActive:false, reason:'Organizer suspended by admin' })}
        >
          Suspend
        </button>
      )}
    </>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className="stat-card">
    <div className="stat-label">{label}</div>
    <div className="stat-value">{value ?? 0}</div>
    {sub && <div className="stat-sub">{sub}</div>}
  </div>
);

function OrganizerModal({ organizer, onClose, onStatus, onTopUp, acting }) {
  const [tab, setTab] = useState('overview');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpNote, setTopUpNote] = useState('');
  if (!organizer) return null;

  const matches = organizer.matches || [];
  const pitches = organizer.pitches || [];
  const wallet = organizer.wallet || {};
  const user = organizer.user || {};
  const stats = organizer.stats || {};
  const completedMatches = stats.completedMatches ?? matches.filter(match => match.status === 'completed').length;
  const hostedMatches = stats.matches ?? matches.length;
  const totalPayout = wallet.totalWithdrawn || stats.totalWithdrawn || 0;
  const totalEarned = wallet.totalEarned || stats.totalEarned || 0;

  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:1000, padding:'24px 16px', overflowY:'auto' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background:'#0d1a1a', borderRadius:16, width:'100%', maxWidth:920, border:'1px solid rgba(255,255,255,0.07)', marginBottom:32, overflow:'hidden' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:700, color:'#fff' }}>{organizer.name || organizer.displayName}</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.45)', marginTop:4 }}>
              {user.email || organizer.email || 'No email'} · {organizer.city || 'No city'}{organizer.country ? `, ${organizer.country}` : ''}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end' }}>
            <span className={statusBadge(organizer.verificationStatus)}>{statusLabel(organizer.verificationStatus)}</span>
            {actionButtons(organizer, onStatus, acting)}
            <button className="btn btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>

        <div style={{ padding:'18px 24px', display:'grid', gridTemplateColumns:'repeat(4, minmax(120px, 1fr))', gap:10 }}>
          <Stat label="Events hosted" value={hostedMatches} sub={`${completedMatches} completed`} />
          <Stat label="Bookings" value={stats.bookings || 0} sub="Players joined" />
          <Stat label="Total payout" value={money(totalPayout, wallet.currency)} sub={`${money(totalEarned, wallet.currency)} earned`} />
          <Stat label="Pitches" value={stats.pitches || pitches.length} sub="Venues added" />
        </div>

        <div style={{ padding:'0 24px', display:'flex', gap:4, borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
          {['overview', 'matches', 'pitches', 'wallet'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{ background:'none', border:'none', cursor:'pointer', padding:'10px 16px', fontSize:12, fontWeight:600, textTransform:'capitalize', color: tab === t ? '#4B9EF5' : 'rgba(255,255,255,0.4)', borderBottom: tab === t ? '2px solid #4B9EF5' : '2px solid transparent' }}
            >
              {t}
            </button>
          ))}
        </div>

        <div style={{ padding:'20px 24px 24px' }}>
          {tab === 'overview' && (
            <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, overflow:'hidden' }}>
              {[
                ['Display name', organizer.displayName],
                ['User role', user.role],
                ['Email', user.email || organizer.email],
                ['Active', user.isActive === false ? 'No' : 'Yes'],
                ['Status', statusLabel(organizer.verificationStatus)],
                ['Tier', organizer.tier],
                ['Onboarding', organizer.onboardingPhase],
                ['Reputation score', organizer.reputationScore],
                ['Phone', user.phoneNumber || organizer.phoneNumber],
                ['City', organizer.city || user.city],
                ['Country', organizer.country || user.country],
                ['Joined', fmtDate(organizer.createdAt)],
                ['Bio', organizer.bio],
              ].map(([label, value], i, arr) => (
                <div key={label} style={{ display:'flex', padding:'11px 16px', borderBottom:i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <div style={{ width:160, fontSize:12, color:'rgba(255,255,255,0.4)', flexShrink:0 }}>{label}</div>
                  <div style={{ fontSize:13, color:value ? '#fff' : 'rgba(255,255,255,0.25)', lineHeight:1.5 }}>{value || '—'}</div>
                </div>
              ))}
            </div>
          )}

          {tab === 'matches' && (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {matches.length === 0 ? <div style={{ color:'rgba(255,255,255,0.35)', fontSize:13 }}>No matches hosted yet</div> : matches.map(match => {
                const bookings = match.bookings || [];
                return (
                  <div key={match.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'14px 16px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', gap:12 }}>
                      <div>
                        <div style={{ color:'#fff', fontSize:14, fontWeight:700 }}>{match.title || match.location}</div>
                        <div style={{ color:'rgba(255,255,255,0.42)', fontSize:12, marginTop:4 }}>
                          {fmtDate(match.kickoffAt)} · {match.format} · {match.skillLevel} · {match.location}
                        </div>
                      </div>
                      <span className="badge badge-gray">{match.status}</span>
                    </div>
                    <div style={{ display:'flex', gap:12, marginTop:10, color:'rgba(255,255,255,0.45)', fontSize:12, flexWrap:'wrap' }}>
                      <span>{bookings.length} bookings</span>
                      <span>{money(match.pricePerPlayer, match.currency)} per player</span>
                      <span>{match.totalSlots} slots</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'pitches' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:10 }}>
              {pitches.length === 0 ? <div style={{ color:'rgba(255,255,255,0.35)', fontSize:13 }}>No pitches added</div> : pitches.map(pitch => (
                <div key={pitch.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'14px 16px' }}>
                  <div style={{ color:'#fff', fontSize:14, fontWeight:700 }}>{pitch.name}</div>
                  <div style={{ color:'rgba(255,255,255,0.42)', fontSize:12, marginTop:4 }}>{pitch.location}</div>
                  <div style={{ color:'#A7F432', fontSize:12, marginTop:10 }}>{money(pitch.price, pitch.currency)}</div>
                </div>
              ))}
            </div>
          )}

          {tab === 'wallet' && (
            <div>
            <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, overflow:'hidden' }}>
              {[
                ['Balance', money(wallet.balance, wallet.currency)],
                ['Total earned', money(wallet.totalEarned, wallet.currency)],
                ['Total payout', money(wallet.totalWithdrawn, wallet.currency)],
                ['Currency', wallet.currency],
              ].map(([label, value], i, arr) => (
                <div key={label} style={{ display:'flex', padding:'11px 16px', borderBottom:i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <div style={{ width:160, fontSize:12, color:'rgba(255,255,255,0.4)', flexShrink:0 }}>{label}</div>
                  <div style={{ fontSize:13, color:value ? '#fff' : 'rgba(255,255,255,0.25)' }}>{value || '—'}</div>
                </div>
              ))}
            </div>
            <form onSubmit={async event => { event.preventDefault(); const ok = await onTopUp(organizer.id, Number(topUpAmount), topUpNote); if (ok) { setTopUpAmount(''); setTopUpNote(''); } }} style={{ marginTop:16, padding:16, border:'1px solid rgba(255,255,255,0.07)', borderRadius:10 }}>
              <div style={{ color:'#fff', fontSize:14, fontWeight:700, marginBottom:5 }}>Top up organizer wallet</div>
              <div style={{ color:'rgba(255,255,255,.42)', fontSize:11, marginBottom:12 }}>This immediately credits the wallet and creates a completed adjustment transaction and audit record.</div>
              <div style={{ display:'grid', gridTemplateColumns:'minmax(0,180px) minmax(0,1fr) auto', gap:9 }}>
                <input type="number" min="0.01" step="0.01" required className="search-input" placeholder={`Amount (${wallet.currency || 'USD'})`} value={topUpAmount} onChange={event => setTopUpAmount(event.target.value)} />
                <input className="search-input" placeholder="Reason or note (optional)" value={topUpNote} onChange={event => setTopUpNote(event.target.value)} />
                <button type="submit" className="btn btn-primary" disabled={acting || Number(topUpAmount) <= 0}>{acting ? 'Crediting…' : 'Top up'}</button>
              </div>
            </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Organizers({ toast }) {
  const { token } = useApp();
  const [organizers, setOrganizers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await getAppOrganizers(token, { page:1, limit:20, search, status:filter, ...params });
      setOrganizers(res.data || []);
      setPagination(res.pagination || null);
    } catch (e) {
      toast?.(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [token, search, filter, toast]);

  useEffect(() => { load({ search:'', status:'' }); }, [token]);

  const openOrganizer = async organizer => {
    setSelected(organizer);
    try {
      const res = await getAppOrganizerById(token, organizer.id);
      setSelected(res.data || organizer);
    } catch (e) {
      toast?.(e.message, 'error');
    }
  };

  const updateStatus = async (id, body) => {
    setActing(true);
    try {
      const res = await updateAppOrganizerStatus(token, id, body);
      toast?.(res.message || 'Organizer updated');
      const refreshed = await getAppOrganizerById(token, id);
      setSelected(refreshed.data);
      load();
    } catch (e) {
      toast?.(e.message, 'error');
    } finally {
      setActing(false);
    }
  };

  const topUpWallet = async (id, amount, note) => {
    if (!Number.isFinite(amount) || amount <= 0) { toast?.('Enter a valid top-up amount.', 'error'); return false; }
    setActing(true);
    try {
      const res = await topUpAppOrganizerWallet(token, id, { amount, note });
      toast?.(res.message || 'Organizer wallet topped up successfully');
      const refreshed = await getAppOrganizerById(token, id);
      setSelected(refreshed.data);
      load();
      return true;
    } catch (e) {
      toast?.(e.message, 'error');
      return false;
    } finally {
      setActing(false);
    }
  };

  const totals = useMemo(() => ({
    total: pagination?.total ?? organizers.length,
    verified: organizers.filter(o => o.verificationStatus === 'verified').length,
    pending: organizers.filter(o => o.verificationStatus === 'pending').length,
    suspended: organizers.filter(o => o.verificationStatus === 'suspended').length,
  }), [organizers, pagination]);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'var(--text-primary)', margin:0 }}>Organizers</h1>
          <p style={{ color:'var(--text-dim)', fontSize:13, margin:'4px 0 0' }}>Organizer profiles, hosted matches, pitches, and wallets</p>
        </div>
      </div>

      <div className="stats-grid">
        <Stat label="Total organizers" value={totals.total} sub="All host profiles" />
        <Stat label="Approved" value={totals.verified} sub="Ready to host" />
        <Stat label="Pending" value={totals.pending} sub="Needs review" />
        <Stat label="Suspended" value={totals.suspended} sub="Restricted" />
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'stretch' }}>
        <form className="toolbar" onSubmit={e => { e.preventDefault(); load({ page:1 }); }} style={{ flex:1, marginBottom:0, flexWrap:'nowrap' }}>
          <input className="search-input" placeholder="Search organizers by name, email, city..." value={search} onChange={e => setSearch(e.target.value)} style={{ height:46 }} />
          <button type="submit" className="btn btn-ghost" style={{ height:46 }}>Search</button>
        </form>
        <select
          className="search-input"
          value={filter}
          onChange={e => { setFilter(e.target.value); load({ page:1, status:e.target.value }); }}
          style={{ flex:'0 0 138px', minWidth:138, height:46, color:'var(--text-primary)', cursor:'pointer' }}
        >
          <option value="">All Status</option>
          <option value="verified">Approved</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      <div className="card" style={{ padding:0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Organizer</th><th>Status</th><th>Matches</th><th>Pitches</th><th>Wallet</th><th>Joined</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'var(--text-dim)' }}>Loading organizers...</td></tr>
              : organizers.length === 0 ? <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'var(--text-dim)' }}>No organizers found</td></tr>
              : organizers.map(organizer => (
                <tr key={organizer.id}>
                  <td>
                    <div style={{ fontWeight:600 }}>{organizer.name || organizer.displayName}</div>
                    <div style={{ fontSize:11, color:'var(--text-dim)' }}>{organizer.email || organizer.user?.email || '—'} · {organizer.city || organizer.country || '—'}</div>
                  </td>
                  <td><span className={statusBadge(organizer.verificationStatus)}>{statusLabel(organizer.verificationStatus)}</span></td>
                  <td style={{ fontSize:12 }}>{organizer.stats?.matches ?? 0}</td>
                  <td style={{ fontSize:12 }}>{organizer.stats?.pitches ?? 0}</td>
                  <td style={{ fontSize:12 }}>{money(organizer.wallet?.balance, organizer.wallet?.currency)}</td>
                  <td style={{ fontSize:12, color:'var(--text-dim)' }}>{fmtDate(organizer.createdAt)}</td>
                  <td>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      <button className="btn btn-ghost" style={{ fontSize:11 }} onClick={() => openOrganizer(organizer)}>View</button>
                      {actionButtons(organizer, updateStatus, acting, 11)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <OrganizerModal organizer={selected} onClose={() => setSelected(null)} onStatus={updateStatus} onTopUp={topUpWallet} acting={acting} />}
    </div>
  );
}
